import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import "./SlideOverPanel.css";

/**
 * Right-side slide-over panel (filters, detail drawers, etc.).
 */
export default function SlideOverPanel({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 400,
  zIndex = 1070,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  className = "",
  bodyClassName = "",
  footerClassName = "",
  titleId: titleIdProp,
}) {
  const genId = useId();
  const titleId = titleIdProp || `slide-over-title-${genId}`;

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (onClose) onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, closeOnEscape, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const showHeader = !!(title || subtitle || onClose);

  return createPortal(
    <div
      className="slide-over-overlay"
      style={{ zIndex }}
      onClick={(e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget && onClose) onClose();
      }}
      role="presentation"
    >
      <div
        className={`slide-over-panel ${className}`.trim()}
        style={{ width: `min(100%, ${width}px)` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {showHeader && (
          <header className="slide-over-header">
            <div className="slide-over-header-main">
              {title && (
                <h2 id={titleId} className="slide-over-title">
                  {title}
                </h2>
              )}
              {subtitle && <p className="slide-over-subtitle">{subtitle}</p>}
            </div>
            {onClose && (
              <button
                type="button"
                className="slide-over-close"
                onClick={onClose}
                aria-label="Close panel"
              >
                ×
              </button>
            )}
          </header>
        )}
        <div className={`slide-over-body ${bodyClassName}`.trim()}>{children}</div>
        {footer ? (
          <footer className={`slide-over-footer ${footerClassName}`.trim()}>{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
